import React, {FunctionComponent, useMemo} from "react";
import KeyboardLayoutManager from '../../lib/music/piano/keyboard-layout-manager';
import { isBlackKey } from '../../lib/music/piano/keyboard';
import Key from "./key";
import useFingeringAnnotations from './use-fingering-annotations';
import Annotations from "./annotations";

interface KeyboardProps {
  className?: string;
  fingeringAnnotations: ReturnType<typeof useFingeringAnnotations>;
  height: number;
  highlightedKeys?: Array<number>;
  keys: Array<number>;
  width: number;
}

const Keyboard: FunctionComponent<KeyboardProps> = (props) => {
  const vm = useVM(props);

  return (
    <div className={vm.className}>
      <svg height={props.height} width={props.width}>
        {/* White keys layer */}
        {vm.keys.filter(k => !isBlackKey(k)).map(k => (
          <Key
            key={k}
            data={{colour: 'white', isActive: false}}
            {...vm.layoutManager.getBounds(k)}
          />
        ))}

        {/* White key Highlights layer */}
        {vm.highlightedKeys.filter(k => !isBlackKey(k)).map(k => (
          <Key
            key={`h${k}`}
            data={{colour: 'white', isActive: true}}
            {...vm.layoutManager.getBounds(k)}
          />
        ))}

        {/* Black keys layer */}
        {vm.keys.filter(isBlackKey).map(k => (
          <Key
            key={k}
            data={{colour: 'black', isActive: false}}
            {...vm.layoutManager.getBounds(k)}
          />
        ))}

        {vm.highlightedKeys.filter(isBlackKey).map(k => (
          <Key
            key={k}
            data={{colour: 'black', isActive: true}}
            {...vm.layoutManager.getBounds(k)}
          />
        ))}

        {/* Black key Highlights layer */}
        {/* {vm.highlightedKeys.filter(isBlackKey).map(k => <Highlight key={`h${k}`} keyboardKey={k} {...vm.layoutManager.getBounds(k)} />)} */}

        {/* Annotations layer */}
        {vm.annotations.map((a, i) => <Annotations bounds={vm.layoutManager.getBounds(a.key)} group={a.annotations} key={i} />)}
      </svg>
    </div>
  )
}

function useVM({className, fingeringAnnotations, height, highlightedKeys = [], keys, width}: KeyboardProps) {
  const cn = className || '';

  const layoutManager = useMemo(
    () => new KeyboardLayoutManager({keys, canvasDimensions: {height, width}}),
    [keys, height, width]
  );

  const annotations = useMemo(
    () => Object.keys(fingeringAnnotations.annotationsByKeyID).map(k => ({
      key: Number(k),
      annotations: [...fingeringAnnotations.annotationsByKeyID[Number(k)]]
    })),
    [fingeringAnnotations]
  )

  return {
    annotations,
    className: cn,
    highlightedKeys,
    keys,
    layoutManager,
  }
}

interface HighlightProps {
  height: number;
  keyboardKey: number;
  width: number;
  x: number;
  y: number;
}

const Highlight: FunctionComponent<HighlightProps> = ({keyboardKey, ...rest}) => {
  return (
    <rect key={`h${keyboardKey}`} {...rest} fill='green' opacity={0.5} />
  );
}

export default Keyboard;
